import { useCallback, useEffect, useState } from "react";
import { fetchWithAuth } from "./authFetch";
import { CREDITS_CHANGED_EVENT } from "./useCredits";

/* The account's plan and what it unlocks, from /api/billing/status. Fetched once per page
   load and shared by every caller; refreshed on focus and when a purchase or plan change
   dispatches `creditsChanged()`. Until it loads, every feature reads as allowed so nothing
   flickers locked for paying customers; the server is the real gate. */
const OPEN = {
  plan: null,
  planName: null,
  fixedRunGoal: null,
  concurrentSearches: 5,
  thirdPartyBoards: true,
  similarSearch: true,
  emailAlerts: true,
  creditRollover: false,
  jobs: true,
  outreach: true,
  research: true,
  boardSearch: true,
};

let cache = null; // { status, features }
let inflight = null;
let retryTimer = null;
const listeners = new Set();

async function load() {
  if (!inflight) {
    inflight = fetchWithAuth("/api/billing/status")
      .then(async (res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (body) {
          cache = { status: body, features: { ...OPEN, ...(body.features || {}) } };
          listeners.forEach((fn) => fn(cache));
        } else if (!cache) {
          // The server may be restarting; try again shortly rather than sitting on the defaults.
          clearTimeout(retryTimer);
          retryTimer = setTimeout(load, 5000);
        }
        return cache;
      })
      .catch(() => {
        if (!cache) {
          clearTimeout(retryTimer);
          retryTimer = setTimeout(load, 5000);
        }
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

function refreshPlan() {
  cache = null;
  return load();
}

export function usePlan() {
  const [state, setState] = useState(cache);
  useEffect(() => {
    const onChange = (next) => setState(next);
    listeners.add(onChange);
    if (!cache) load();
    const onCredits = () => refreshPlan();
    // A plan change made in another tab, or a reload after a server restart, shows up on focus.
    const onFocus = () => load();
    window.addEventListener(CREDITS_CHANGED_EVENT, onCredits);
    window.addEventListener("focus", onFocus);
    return () => {
      listeners.delete(onChange);
      window.removeEventListener(CREDITS_CHANGED_EVENT, onCredits);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
  const refresh = useCallback(() => refreshPlan(), []);
  return {
    loading: !state,
    status: state?.status || null,
    features: state?.features || OPEN,
    refresh,
  };
}
